/**
 * Wave 29 Chunk 5 — /contracts hub page (HR-only).
 *
 * List contracts, create new, drill into detail for signature management.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  FileSignature, Plus, ChevronLeft, Loader2, X, Send, Archive,
  CheckCircle2,
} from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "../components/ui/sonner";
import VoiceTextarea from "../components/VoiceTextarea";

const KINDS = [
  { value: "custom", label: "Custom" },
  { value: "employment", label: "Employment" },
  { value: "nda", label: "NDA" },
  { value: "supplier", label: "Supplier" },
];

export default function Contracts() {
  const { user } = useAuth();
  const isHr = user && ["owner", "admin"].includes(user.role);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(false);
  const [composerOpen, setComposerOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [kind, setKind] = useState("custom");
  const [body, setBody] = useState("");
  const [counterpartyName, setCounterpartyName] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/contracts", { params: { archived: showArchived } });
      setItems(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load contracts.");
    } finally { setLoading(false); }
  };
  useEffect(() => { if (isHr) load(); /* eslint-disable-next-line */ }, [showArchived, isHr]);

  const submit = async () => {
    if (!title.trim() || !body.trim()) return toast.error("Title and body required.");
    setSaving(true);
    try {
      await api.post("/contracts", { title: title.trim(), kind, body: body.trim(), counterparty_name: counterpartyName.trim() || null });
      toast.success("Contract created.");
      setComposerOpen(false); setTitle(""); setBody(""); setKind("custom"); setCounterpartyName("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally { setSaving(false); }
  };

  if (!user) return null;
  if (!isHr) return (
    <div className="hv-card p-6 text-center text-sm text-[#a0aabf]" data-testid="contracts-no-access">
      Contracts are HR-only. Talk to an owner or admin.
    </div>
  );

  return (
    <div className="space-y-4" data-testid="contracts-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <Link to="/team" className="hv-btn hv-btn-ghost text-xs">
          <ChevronLeft className="w-3.5 h-3.5" /> Back
        </Link>
        <h1 className="font-display text-xl sm:text-2xl font-bold flex items-center gap-2">
          <FileSignature className="w-5 h-5 text-[#1FB6C1]" /> Contracts
        </h1>
        <button onClick={() => setComposerOpen(true)} className="hv-btn hv-btn-primary text-xs" data-testid="contracts-new-btn">
          <Plus className="w-3.5 h-3.5" /> New
        </button>
      </div>

      <div className="flex items-center gap-2 text-xs">
        <label className="inline-flex items-center gap-1 text-[#a0aabf] cursor-pointer">
          <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} data-testid="contracts-show-archived" />
          Show archived
        </label>
        <span className="text-[#647087] ml-auto">{items.length} contract{items.length === 1 ? "" : "s"}</span>
      </div>

      {composerOpen && (
        <div className="hv-card p-4 space-y-3" data-testid="contract-composer">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold">New contract</h3>
            <button onClick={() => setComposerOpen(false)} className="text-[#647087]"><X className="w-4 h-4" /></button>
          </div>
          <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title" className="hv-input text-sm w-full" data-testid="contract-title" />
          <div className="flex gap-1 flex-wrap">
            {KINDS.map((k) => (
              <button
                key={k.value}
                type="button"
                onClick={() => setKind(k.value)}
                className={`text-[11px] px-2.5 py-1 rounded-full border ${kind === k.value ? "bg-[#1FB6C1] text-[#0a0b0e] border-[#1FB6C1]" : "border-white/15 text-[#a0aabf]"}`}
                data-testid={`contract-kind-${k.value}`}
              >
                {k.label}
              </button>
            ))}
          </div>
          <input type="text" value={counterpartyName} onChange={(e) => setCounterpartyName(e.target.value)} placeholder="Counterparty name (optional)" className="hv-input text-sm w-full" />
          <VoiceTextarea value={body} onChange={setBody} rows={8} placeholder="Contract body / terms…" data-testid="contract-body"  testIdPrefix="contract-body-voice" context="note" />
          <button onClick={submit} disabled={saving} className="hv-btn hv-btn-primary text-xs disabled:opacity-50" data-testid="contract-save">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Save
          </button>
        </div>
      )}

      {loading ? (
        <div className="h-40 hv-card animate-pulse" />
      ) : items.length === 0 ? (
        <div className="hv-card p-8 text-center text-sm text-[#647087]">
          <FileSignature className="w-8 h-8 mx-auto mb-2 opacity-30" />
          {showArchived ? "No archived contracts." : "No contracts yet. Create the first one."}
        </div>
      ) : (
        <div className="space-y-2" data-testid="contracts-list">
          {items.map((c) => (
            <Link
              key={c.id}
              to={`/contracts/${c.id}`}
              className="hv-card p-4 block hover:border-[#1FB6C1]/40 transition-colors"
              data-testid={`contract-${c.id}`}
            >
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-display text-base font-bold">{c.title}</h3>
                    <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-white/[0.06] text-[#a0aabf]">{c.kind}</span>
                    {c.archived && <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-400/15 text-amber-300 border border-amber-400/30 inline-flex items-center gap-0.5"><Archive className="w-2.5 h-2.5" /> archived</span>}
                  </div>
                  <div className="text-[10px] text-[#647087] mt-0.5">
                    by {c.created_by_name} · {new Date(c.created_at).toLocaleDateString("en-ZA")}
                    {c.counterparty_name && <span> · for {c.counterparty_name}</span>}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-[10px] text-[#647087]">Signatures</div>
                  <div className="text-base font-semibold inline-flex items-center gap-1">
                    <CheckCircle2 className={`w-3.5 h-3.5 ${c.signed_count === c.signature_count && c.signature_count > 0 ? "text-emerald-300" : "text-[#647087]"}`} />
                    <span className="tabular-nums">{c.signed_count || 0}/{c.signature_count || 0}</span>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
